import moment from 'moment';
import { ClockCircleFilled } from '@ant-design/icons';
import { Space, Table, TableColumnsType, Tooltip, Typography } from 'antd';
import { Invoice, InvoicesStatuses, ListInvoicesSortParams } from 'store/invoices/types';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import TooltipWithCopy from 'components/ui/composed/tooltipWithCopy/TooltipWithCopy';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import InvoiceStatus from 'views/invoices/components/InvoiceStatus';
import UserAvatar from 'components/new-ui/base/avatar/UserAvatar';
import ActionsButtonWithPopover from './ActionsButtonWithPopover';
import { getDueFlags } from 'views/invoices/utils';
import ExportIconOutlined from 'components/icons/ExportIconOutlined';
import EmptyCell from 'components/new-ui/base/emtpy-cell/EmptyCell';
import { TableProps } from 'antd/es/table';
import { SortDirection, toAntOrder } from 'utils/tableHelpers';
import useInvoicesConfig from 'hooks/useInvoicesConfig';
import EmailNotificationStatusIcon from 'components/ui/composed/email-notification/EmailNotificationStatusIcon';

type Props = Omit<TableProps<Invoice>, 'dataSource' | 'columns' | 'rowKey'> & {
    data: Invoice[];
    showActions?: boolean;
    actions?: {
        duplicate?: (invoiceId: string) => void;
        downloadPdf?: (invoiceId: string, orderNumber: string) => void;
        archive?: (invoiceId: string) => void;
        unarchive?: (invoiceId: string) => void;
        exportCsv?: (invoiceId: string, orderNumber: string) => void;
    };
    currentSortParam?: ListInvoicesSortParams;
    currentSortDirection?: SortDirection;
};

const InvoicesTable = ({
    data,
    showActions = true,
    actions,
    currentSortParam,
    currentSortDirection,
    ...rest
}: Props) => {
    const { config } = useInvoicesConfig();
    const dueDaysCountForWarning = config?.dueDateAlert?.amount ?? 30;

    const columns: TableColumnsType<Invoice> = [
        {
            render: (_, invoice) => {
                const dataTestId = 'invoices-table-error-notifications-row';

                const { isOverdue, isDueSoon, diffDays } = getDueFlags(
                    invoice.dueDate,
                    dueDaysCountForWarning,
                    invoice.status
                );
                if ((!isOverdue && !isDueSoon) || diffDays === null) return null;

                const title = isOverdue
                    ? `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'}`
                    : `Due in ${diffDays} day${diffDays === 1 ? '' : 's'}`;

                return (
                    <Tooltip data-testid={dataTestId} title={title}>
                        <ClockCircleFilled
                            style={{
                                fontSize: 16,
                                color: 'var(--dust-red-6)',
                                verticalAlign: 'middle',
                            }}
                        />
                    </Tooltip>
                );
            },
            width: 32,
        },
        {
            title: 'Invoice Number',
            key: 'INVOICE_NUMBER',
            dataIndex: 'invoiceNumber',

            render: (invoiceNumber?: string) => {
                const dataTestId = 'invoices-table-invoiceNumber-row';

                if (!invoiceNumber) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return (
                    <TooltipWithCopy copyText={invoiceNumber} dataTestId={dataTestId}>
                        {invoiceNumber}
                    </TooltipWithCopy>
                );
            },
        },
        {
            title: 'Customer',
            key: 'CUSTOMER',
            render: (_, invoice) => {
                const dataTestId = 'invoices-table-customer-row';
                return (
                    <Typography.Text style={{ whiteSpace: 'nowrap' }} data-testid={dataTestId}>
                        {invoice.customer.name}
                    </Typography.Text>
                );
            },
        },
        {
            title: 'Order Number',
            key: 'ORDER_NUMBER',
            dataIndex: 'orderNumber',
            sorter: true,
            sortOrder:
                currentSortParam === ListInvoicesSortParams.ORDER_NUMBER ? toAntOrder(currentSortDirection) : null,
            render: (orderNumber: string) => {
                const dataTestId = 'invoices-table-orderNumber-row';
                return (
                    <TooltipWithCopy copyText={orderNumber} dataTestId={dataTestId}>
                        {orderNumber}
                    </TooltipWithCopy>
                );
            },
        },
        {
            title: 'Job Ref Number',
            key: 'JOB_REFERENCE_ID',
            dataIndex: 'jobReferenceId',
            sorter: true,
            sortOrder:
                currentSortParam === ListInvoicesSortParams.JOB_REFERENCE_ID ? toAntOrder(currentSortDirection) : null,
            render: (_, invoice) => {
                const dataTestId = 'invoices-table-jobReferenceNum-row';

                const to = invoice?.manifestId
                    ? `/manifests/${invoice.manifestId}/${invoice.jobId}`
                    : `/jobs/${invoice.jobId}`;
                return (
                    <Typography.Link
                        style={{ whiteSpace: 'nowrap' }}
                        href={to}
                        data-testid={dataTestId}
                        className="no-row-nav"
                    >
                        {invoice.jobReferenceId}
                    </Typography.Link>
                );
            },
        },
        {
            title: 'Amount',
            key: 'TOTAL_AMOUNT',
            dataIndex: 'totalAmount',
            sorter: true,
            align: 'right',
            sortOrder:
                currentSortParam === ListInvoicesSortParams.TOTAL_AMOUNT ? toAntOrder(currentSortDirection) : null,
            render: (totalAmount: number) => {
                const dataTestId = 'invoices-table-amount-row';
                return <Typography.Text data-testid={dataTestId}>{totalAmount}</Typography.Text>;
            },
        },
        {
            title: 'Created',
            key: 'CREATED_AT',
            dataIndex: 'createdAt',
            sorter: true,
            sortOrder: currentSortParam === ListInvoicesSortParams.CREATED_AT ? toAntOrder(currentSortDirection) : null,
            render: (_, invoice) => {
                const dataTestId = 'invoices-table-createdBy-row';
                const name = invoice.issuer?.fullName ?? '-';
                const imageUrl = invoice.issuer?.profileLink ?? '-';
                const date = moment(invoice.createdAt);
                const formattedDate = date.format('DD/MM/YY');
                const formattedTime = date.format('HH:mm A');

                return (
                    <div data-testid={dataTestId} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {invoice.issuer ? (
                            <Space size={4} style={{ whiteSpace: 'nowrap' }}>
                                <UserAvatar size="small" userName={name} src={imageUrl}>
                                    {name}
                                </UserAvatar>
                                <Typography.Text>{name.split(' ')[0]}</Typography.Text>
                            </Space>
                        ) : null}

                        <Typography.Text style={{ whiteSpace: 'nowrap' }}>
                            {formattedDate}, {formattedTime}
                        </Typography.Text>
                    </div>
                );
            },
        },
        {
            width: 1,
            key: 'EMAIL_NOTIFICATIONS',
            render: (_, record) => {
                const dataTestId = 'invoice-emailNotification-row';
                const overallStatus = record?.sendToCustomerEmailStatus?.overallStatus;
                const providerStatus = record?.sendToCustomerEmailStatus?.overallProviderEventType;

                if (!overallStatus) return null;

                return (
                    <EmailNotificationStatusIcon
                        overallStatus={overallStatus}
                        providerStatus={providerStatus}
                        dataTestId={dataTestId}
                    />
                );
            },
        },
        {
            title: 'Status',
            key: 'STATUS',
            dataIndex: 'status',
            sorter: true,
            sortOrder: currentSortParam === ListInvoicesSortParams.STATUS ? toAntOrder(currentSortDirection) : null,
            render: (_, invoice) => {
                const dataTestId = 'invoices-table-status-row';
                const dueDate = invoice.dueDate;

                return (
                    <InvoiceStatus
                        status={invoice.status}
                        dataTestId={dataTestId}
                        due={{
                            dueDate,
                            dueDaysCountForWarning,
                        }}
                    />
                );
            },
        },
        {
            title: 'Actions',
            key: 'ACTIONS',
            render: (_, invoice) => {
                const dataTestId = 'invoices-table-actions-row';
                const isArchived = invoice.archived;
                const allowedArchiveStatuses = [InvoicesStatuses.PAID, InvoicesStatuses.CANCELLED];
                const allowArchive = allowedArchiveStatuses.includes(invoice.status) && !isArchived;

                return (
                    <Space data-testid={dataTestId} size={8}>
                        {actions?.exportCsv && (
                            <IconButton
                                icon={<ExportIconOutlined />}
                                tooltipText="Export CSV"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    actions?.exportCsv?.(invoice.id, invoice.orderNumber);
                                }}
                            />
                        )}

                        {actions && (
                            <ActionsButtonWithPopover
                                onDuplicate={actions.duplicate ? () => actions?.duplicate?.(invoice.id) : undefined}
                                onDownloadPdf={
                                    actions?.downloadPdf
                                        ? () => actions?.downloadPdf?.(invoice.id, invoice.orderNumber)
                                        : undefined
                                }
                                onArchive={
                                    allowArchive && actions.archive ? () => actions?.archive?.(invoice.id) : undefined
                                }
                                onUnarchive={
                                    isArchived && actions.unarchive ? () => actions?.unarchive?.(invoice.id) : undefined
                                }
                            />
                        )}
                    </Space>
                );
            },
        },
    ];

    const hasEmailNotifications = data.some((invoice) => !!invoice?.sendToCustomerEmailStatus?.overallStatus);

    const filteredColumns = columns.filter((c) => {
        if (c.key === 'ACTIONS' && !showActions) return false;
        if (c.key === 'EMAIL_NOTIFICATIONS' && !hasEmailNotifications) return false;
        return true;
    });

    return (
        <StyledTableWrapper>
            <Table<Invoice>
                rowKey="id"
                rowClassName={(record) => {
                    const { isOverdue, isDueSoon } = getDueFlags(record.dueDate, dueDaysCountForWarning, record.status);
                    if (isOverdue || isDueSoon) return 'row-align-top row-error';
                    return 'row-align-top';
                }}
                dataSource={data}
                columns={filteredColumns}
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
                scroll={{ x: 'max-content' }}
                {...rest}
            />
        </StyledTableWrapper>
    );
};

export default InvoicesTable;
